import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RefreshCw, Loader2 } from 'lucide-react';
import {
  Clock,        // Today 改用 Clock 圖示，更符合時間概念
  CalendarDays, // Calendar: 日曆含日期，比空白日曆更有辨識度
  ListChecks,   // Tasks: 勾選清單，比單一勾選圖標更能表達「清單」概念
  FilePen,      // Captures: 筆記圖標
  Flag          // Goals: 旗幟象徵目標和里程碑
} from '../common/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useServiceWorkerUpdate } from '../../hooks/useServiceWorkerUpdate';
import logo from '../../assets/images/Logo-Font-Color-NB.png';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  // 獲取當前路徑的第一個部分
  const activePath = '/' + (currentPath.split('/')[1] || '');

  const { currentUser } = useAuth();
  const { isUpdateAvailable, isUpdating, applyUpdate } = useServiceWorkerUpdate();

  // 導航項目數據 - 移除 Profile，改為底部用戶區域
  const navItems = [
    { path: '/today', label: 'Today', icon: <Clock className="h-5 w-5" /> },
    { path: '/captures', label: 'Captures', icon: <FilePen className="h-5 w-5" /> }, 
    { path: '/tasks', label: 'Tasks', icon: <ListChecks className="h-5 w-5" /> },
    { path: '/calendar', label: 'Calendar', icon: <CalendarDays className="h-5 w-5" /> },
    { path: '/goals', label: 'Goals', icon: <Flag className="h-5 w-5" /> }
  ];

  // 獲取用戶顯示名稱
  const getUserDisplayName = () => {
    if (currentUser?.displayName) return currentUser.displayName;
    if (currentUser?.email) {
      return currentUser.email.split('@')[0];
    }
    return 'User';
  };

  // 獲取用戶頭像字母
  const getUserInitial = () => {
    const name = getUserDisplayName();
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="flex flex-col w-64 h-full bg-white border-r border-gray-200">
      {/* Logo 區域 */}
      <div className="p-6 border-b border-gray-200">
        <img 
          src={logo} 
          alt="Orlo" 
          className="h-8 mb-3 -ml-1"
        />
        <p className="text-xs text-gray-500">From Vision to Action</p>
      </div>
      
      {/* 導航區域 */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto hide-scrollbar">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`${
              activePath === item.path
                ? 'bg-sky-50 text-sky-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } group flex items-center px-4 py-3 mb-2 text-sm font-semibold rounded-lg transition-all duration-200`}
          >
            <div className={`mr-3 ${
              activePath === item.path ? 'text-sky-600' : 'text-gray-600'
            }`}>{item.icon}</div>
            {item.label}
          </Link>
        ))}
      </nav>
      
      {/* 更新提示 card（有新版本時顯示） */}
      {isUpdateAvailable && (
        <div className="mx-3 mb-3 p-3 rounded-lg bg-sky-50 border border-sky-200 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-sky-600 shrink-0" />
          <span className="flex-1 text-sm font-medium text-sky-600">Update available</span>
          <button
            onClick={applyUpdate}
            disabled={isUpdating}
            aria-busy={isUpdating}
            aria-label={isUpdating ? 'Updating...' : 'Update'}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-sky-600 bg-white border border-sky-300 rounded-md hover:bg-sky-50 disabled:opacity-70 transition-colors"
          >
            {isUpdating ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              'Update'
            )}
          </button>
        </div>
      )}

      {/* 用戶資訊區域 */}
      <div className="p-4 shadow-[0_-1px_3px_0_rgba(0,0,0,0.05)]">
        <Link
          to="/profile"
          className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-all duration-200"
        >
          {/* 用戶頭像 */}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center text-white font-semibold mr-3">
            {currentUser?.photoURL ? (
              <img 
                src={currentUser.photoURL} 
                alt={getUserDisplayName()}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              getUserInitial()
            )}
          </div>
          
          {/* 用戶資訊 */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {getUserDisplayName()}
            </p>
            {currentUser?.email && (
              <p className="text-xs text-gray-500 truncate">
                {currentUser.email}
              </p>
            )}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Sidebar;